import React, { useCallback, useEffect, useRef, useState } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";
import PosterEditor from "@/components/admin/PosterEditor";

// WW-160.10 · the review desk's Edit poster, driven off the event's review row
// (same id). Reports the cover + whether a person confirmed it.
export const PublishedPosterEditor = ({ eventId, onChange, onClose }) => {
  const [row, setRow] = useState(null);
  const [missing, setMissing] = useState(false);
  const enc = encodeURIComponent(eventId);
  // The first load only reads the row; the drawer adopts a cover only after an edit here.
  const edited = useRef(false);

  const reload = useCallback(async () => {
    try {
      const r = await client.get(`/admin/queue/${enc}`);
      setRow(r.data.pending);
      if (edited.current) onChange(r.data.pending.cover_image_url, r.data.pending.poster_gate?.state === "confirmed");
    } catch { setMissing(true); }
  }, [enc, onChange]);
  useEffect(() => { reload(); }, [reload]);
  const reloadEdited = useCallback(async () => { edited.current = true; await reload(); }, [reload]);

  const act = (fn, ok) => async (...args) => {
    try { await fn(...args); if (ok) toast.success(ok); } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.message || "failed"); }
    await reloadEdited();
  };

  if (missing) {
    return (
      <div data-testid="published-poster-no-row" className="mt-3 rounded-md border p-3 text-xs lowercase"
           style={{ borderColor: "var(--line-night)", color: "#F8B88B" }}>
        this event has no review row, so the poster can only be changed by url.
        <button onClick={onClose} className="ml-2 underline">close</button>
      </div>
    );
  }
  if (!row) return <p className="mt-3 text-xs" style={{ color: "var(--text-dim-night)" }}>loading poster...</p>;

  const drives = Array.from(new Set([
    ...(row.raw_inputs || []).flatMap((r) => r?.source_metadata?.drive_file_ids || []),
    ...(row.merged_drive_file_ids || []),
    ...(row.poster_classifications || []).map((c) => c.drive_file_id),
  ].filter(Boolean)));

  return (
    <div data-testid="published-poster-editor" style={{ position: "fixed", inset: 0, zIndex: 80, background: "#FBF5EA", overflow: "auto" }}>
      <PosterEditor
        key={`pub-editor-${row.id}`}
        pendingId={row.id}
        driveFileId={drives[0] || ""}
        initialBbox={row.cover_bbox}
        coverUrl={row.cover_image_url}
        cropBaseCoverUrl={row.crop_base_cover_url || ""}
        originalCoverUrl={row.original_cover_image_url}
        defaultTab={drives.length ? "crop" : "replace"}
        posterConfirmed={row.poster_gate?.state === "confirmed"}
        posterGate={row.poster_gate}
        posterCheck={row.poster_check}
        coverFingerprint={row.cover_image_url || ""}
        availableDriveFiles={drives}
        onClose={onClose}
        onCropSaved={reloadEdited}
        onPatchApplied={reloadEdited}
        onPatchRestored={reloadEdited}
        onNoPoster={onClose}
        onReplaceUploaded={act(async (file) => {
          const fd = new FormData();
          fd.append("file", file);
          const r = await client.post(`/admin/drive-watcher/upload-cover/${enc}`, fd, { timeout: 60000, headers: { "Content-Type": "multipart/form-data" } });
          if (!r.data?.ok) throw new Error(r.data?.error || "upload failed");
        }, "uploaded · confirm the new poster now")}
        onReplaceDrivePicked={act((did) => client.post(`/admin/drive-watcher/manual-crop/${enc}`, { drive_file_id: did, bbox: { left: 0, top: 0, right: 1, bottom: 1 } }), "switched screenshot · crop and confirm now")}
        onPosterConfirmed={act(() => client.post(`/admin/queue/${enc}/poster-confirm`), "poster confirmed")}
        onPosterUnconfirmed={act(() => client.post(`/admin/queue/${enc}/poster-unconfirm`), "undone")}
      />
    </div>
  );
};
